import { useState, useEffect } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addDays,
  startOfWeek,
  endOfWeek,
  addHours,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Filter,
  Search,
  FileText,
  AlertCircle,
  MessageSquare,
  TrendingUp,
  BookOpen,
  FileCheck,
  Shield,
  Users,
  Loader2,
  FolderOpen,
  CheckSquare,
  Clock,
  Trash2,
  Edit,
  Gavel,
} from "lucide-react";
import CalendarioProcesalDespachos from "@/pages/dashboard/despachos/components/CalendarioProcesalDespachos";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NewEventDialog } from "@/components/NewEventDialog";
import { Event, EventType } from "@/types/event";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useUser } from "@/hooks/useUser";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { Database } from "@/lib/database.types";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useSession } from "@supabase/auth-helpers-react";

type DatabaseEventType = Database["public"]["Enums"]["event_type"];

// Typed aliases for case management tables
type LegalCaseRow = Database["public"]["Tables"]["procesos"]["Row"];
type LegalTaskRow = Database["public"]["Tables"]["legal_tasks"]["Row"];
type LegalDeadlineRow = Database["public"]["Tables"]["legal_deadlines"]["Row"];

const mapDatabaseTypeToEventType = (type: DatabaseEventType): EventType => {
  switch (type) {
    case "meeting":
      return "Reunión";
    case "document_review":
      return "Documento";
    case "deadline":
      return "Vencimiento";
    case "consultation":
      return "Consulta";
    case "court_date":
      return "Vencimiento";
    default:
      return "Reunión";
  }
};

const mapEventTypeToDatabase = (
  type: EventType
): Database["public"]["Enums"]["event_type"] => {
  switch (type) {
    case "Reunión":
      return "meeting";
    case "Documento":
      return "document_review";
    case "Vencimiento":
      return "deadline";
    case "Consulta":
      return "consultation";
    default:
      return "meeting";
  }
};

const mapEventTypeFromDatabase = (
  type: Database["public"]["Enums"]["event_type"]
): EventType => {
  switch (type) {
    case "meeting":
      return "Reunión";
    case "document_review":
      return "Documento";
    case "deadline":
      return "Vencimiento";
    case "consultation":
      return "Consulta";
    case "court_date":
      return "Vencimiento";
    default:
      return "Reunión";
  }
};

interface SupabaseEvent {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  event_type: DatabaseEventType;
  original_event_type: string | null;
  start_time: string;
  end_time: string;
  location: string | null;
  created_at: string;
  updated_at: string;
}

export function Calendar() {
  const { user } = useUser();
  const [viewCategory, setViewCategory] = useState<"procesal" | "general">("procesal");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("month");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEventType, setSelectedEventType] = useState<string | null>(
    null
  );
  const [isNewEventDialogOpen, setIsNewEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<Event | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [cases, setCases] = useState<LegalCaseRow[]>([]);
  const [tasks, setTasks] = useState<LegalTaskRow[]>([]);
  const [deadlines, setDeadlines] = useState<LegalDeadlineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadEvents();
      loadCaseManagementData();
    }
  }, [user]);

  const loadEvents = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("calendar_events")
        .select("*")
        .eq("user_id", user.id)
        .order("start_time", { ascending: true });

      if (error) {
        console.error("Error loading events:", error);
        toast({
          title: "Error",
          description: "Error al cargar los eventos",
          variant: "destructive",
        });
        return;
      }

      // Map database events to our Event interface
      const mappedEvents: Event[] = (data || []).map(
        (event: SupabaseEvent) => ({
          id: event.id,
          title: event.title,
          type:
            (event.original_event_type as EventType) ||
            mapEventTypeFromDatabase(event.event_type),
          event_type: event.event_type,
          start_time: event.start_time,
          end_time: event.end_time,
          description: event.description || "",
          location: event.location || undefined,
          status: "pending",
        })
      );

      setEvents(mappedEvents);
    } catch (error) {
      console.error("Error loading events:", error);
      toast({
        title: "Error",
        description: "Error al cargar los eventos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCaseManagementData = async () => {
    if (!user) return;

    try {
      // Load cases
      const { data: casesData, error: casesError } = await supabase
        .from("procesos")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (casesError) {
        console.error("Error loading cases:", casesError);
      } else {
        setCases(casesData || []);
      }

      // Load tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from("legal_tasks")
        .select("*")
        .eq("user_id", user.id)
        .order("due_date", { ascending: true });

      if (tasksError) {
        console.error("Error loading tasks:", tasksError);
      } else {
        setTasks(tasksData || []);
      }

      // Load deadlines
      const { data: deadlinesData, error: deadlinesError } = await supabase
        .from("legal_deadlines")
        .select("*")
        .eq("user_id", user.id)
        .order("due_date", { ascending: true });

      if (deadlinesError) {
        console.error("Error loading deadlines:", deadlinesError);
      } else {
        setDeadlines(deadlinesData || []);
      }
    } catch (error) {
      console.error("Error loading case management data:", error);
    }
  };

  // Convert case management data to calendar events
  const convertCasesToEvents = (cases: LegalCaseRow[]): Event[] => {
    return cases.map((caseItem: any) => {
      const meta = caseItem.metadata || {};
      return {
        id: `case-${caseItem.id}`,
        title: caseItem.asunto || caseItem.clase || 'Proceso sin título',
        type: "Caso" as EventType,
        event_type: "meeting" as DatabaseEventType, // Using meeting as base type
        original_event_type: "Caso",
        start_time: caseItem.created_at,
        end_time: caseItem.created_at,
        description: meta.description || caseItem.tipo_proceso || "",
        clientName: meta.client_name || "",
        status: "pending" as const,
        // Store original data for editing
        originalData: {
          id: caseItem.id,
          caseNumber: caseItem.numero_proceso,
          clientName: meta.client_name || "",
          clientEmail: meta.client_email || "",
          clientPhone: meta.client_phone || "",
          caseStatus: caseItem.watch_status || "active",
        },
      };
    });
  };

  const convertTasksToEvents = (tasks: LegalTaskRow[]): Event[] => {
    return tasks
      .filter((task) => task.due_date)
      .map((task) => ({
        id: `task-${task.id}`,
        title: task.title,
        type: "Tarea" as EventType,
        event_type: "deadline" as DatabaseEventType,
        original_event_type: "Tarea",
        start_time: task.due_date as string,
        end_time: task.due_date as string,
        description: task.description || "",
        status: task.status === "completed" ? "completed" : "pending",
        // Store original data for editing
        originalData: {
          id: task.id,
          taskPriority: task.priority,
          taskStatus: task.status,
        },
      }));
  };

  const convertDeadlinesToEvents = (deadlines: LegalDeadlineRow[]): Event[] => {
    return deadlines.map((deadline) => ({
      id: `deadline-${deadline.id}`,
      title: deadline.title,
      type: "Plazo" as EventType,
      event_type: "deadline" as DatabaseEventType,
      original_event_type: "Plazo",
      start_time: deadline.due_date,
      end_time: deadline.due_date,
      description: deadline.description || "",
      status: deadline.status === "completed" ? "completed" : "pending",
      // Store original data for editing
      originalData: {
        id: deadline.id,
        reminderDays: deadline.reminder_days,
        deadlineStatus: deadline.status,
      },
    }));
  };

  // Navigation functions
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));

  // Combine all events including case management data
  const allEvents = [
    ...events,
    ...convertCasesToEvents(cases),
    ...convertTasksToEvents(tasks),
    ...convertDeadlinesToEvents(deadlines),
  ];

  // Filter events based on search query and selected type
  const filteredEvents = allEvents
    .filter((event) => {
      const matchesSearch =
        searchQuery === "" ||
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.location?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType =
        !selectedEventType ||
        selectedEventType === "all" ||
        event.type.toLowerCase() === selectedEventType?.toLowerCase();

      return matchesSearch && matchesType;
    })
    .sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return filteredEvents.filter((event) =>
      isSameDay(new Date(event.start_time), day)
    );
  };

  // Event badge helper function
  const getEventBadge = (type: EventType) => {
    switch (type) {
      case "Reunión":
        return (
          <Badge variant="outline" className="bg-blue-100 text-blue-800">
            Reunión
          </Badge>
        );
      case "Documento":
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800">
            Documento
          </Badge>
        );
      case "Vencimiento":
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800">
            Vencimiento
          </Badge>
        );
      case "Consulta":
        return (
          <Badge variant="outline" className="bg-purple-100 text-purple-800">
            Consulta
          </Badge>
        );
      case "Caso":
        return (
          <Badge variant="outline" className="bg-orange-100 text-orange-800">
            Caso
          </Badge>
        );
      case "Tarea":
        return (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
            Tarea
          </Badge>
        );
      case "Plazo":
        return (
          <Badge variant="outline" className="bg-indigo-100 text-indigo-800">
            Plazo
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleDeleteEvent = async (event: Event) => {
    setDeletingEvent(event);
  };

  const confirmDeleteEvent = async () => {
    if (!user || !deletingEvent) return;

    try {
      let result;
      let actualId = deletingEvent.id;

      // Extract the actual ID from prefixed IDs for case management events
      if (deletingEvent.id.startsWith("case-")) {
        actualId = deletingEvent.id.replace("case-", "");
      } else if (deletingEvent.id.startsWith("task-")) {
        actualId = deletingEvent.id.replace("task-", "");
      } else if (deletingEvent.id.startsWith("deadline-")) {
        actualId = deletingEvent.id.replace("deadline-", "");
      }

      // Delete from the appropriate table based on event type
      switch (deletingEvent.type) {
        case "Caso":
          result = await supabase
            .from("procesos")
            .delete()
            .eq("id", actualId)
            .eq("user_id", user.id);
          break;

        case "Tarea":
          result = await supabase
            .from("legal_tasks")
            .delete()
            .eq("id", actualId)
            .eq("user_id", user.id);
          break;

        case "Plazo":
          result = await supabase
            .from("legal_deadlines")
            .delete()
            .eq("id", actualId)
            .eq("user_id", user.id);
          break;

        default:
          // For regular calendar events
          result = await supabase
            .from("calendar_events")
            .delete()
            .eq("id", actualId)
            .eq("user_id", user.id);
          break;
      }

      if (result.error) {
        console.error("Error deleting event:", result.error);
        toast({
          title: "Error",
          description: "Error al eliminar el evento",
          variant: "destructive",
        });
        return;
      }

      // Refresh the appropriate data
      if (["Caso", "Tarea", "Plazo"].includes(deletingEvent.type)) {
        await loadCaseManagementData();
      } else {
        await loadEvents();
      }

      toast({
        title: "Éxito",
        description: "Evento eliminado exitosamente",
      });

      setDeletingEvent(null);
    } catch (error) {
      console.error("Error deleting event:", error);
      toast({
        title: "Error",
        description: "Error inesperado al eliminar el evento",
        variant: "destructive",
      });
    }
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setIsNewEventDialogOpen(true);
  };

  const handleEventSubmit = async (event: Event) => {
    try {
      // For case management events, refresh the case management data
      if (["Caso", "Tarea", "Plazo"].includes(event.type)) {
        await loadCaseManagementData();
      } else {
        // For regular calendar events, just refresh the list since NewEventDialog has already saved it
        await loadEvents();
      }

      setIsNewEventDialogOpen(false);
      setEditingEvent(null);
    } catch (error) {
      console.error("Error updating event list:", error);
    }
  };

  // Get calendar days
  const getCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const MonthView = () => {
    const days = getCalendarDays();

    return (
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
        {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((day) => (
          <div
            key={day}
            className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}

        {days.map((day) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isCurrentDay = isToday(day);

          return (
            <div
              key={day.toString()}
              className={cn(
                "min-h-[100px] p-2 bg-white",
                !isCurrentMonth && "text-gray-400"
              )}>
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "text-sm",
                    isCurrentDay &&
                      "bg-legal-blue text-white rounded-full w-6 h-6 flex items-center justify-center"
                  )}>
                  {format(day, "d")}
                </span>
                {dayEvents.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {dayEvents.length}
                  </Badge>
                )}
              </div>

              <div className="mt-1 space-y-1">
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    className="text-xs p-1 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">
                        {event.title}
                      </span>
                      {getEventBadge(event.type)}
                    </div>
                    <div className="text-gray-500 text-[10px] mt-1">
                      {format(new Date(event.start_time), "HH:mm")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const WeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = eachDayOfInterval({
      start: weekStart,
      end: addDays(weekStart, 6),
    });

    return (
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
        {weekDays.map((day) => (
          <div key={day.toString()} className="bg-white p-2">
            <div className="text-sm font-medium text-gray-500 mb-2">
              {format(day, "EEE", { locale: es })}
            </div>
            <div className="text-lg font-bold">{format(day, "d")}</div>
            <div className="mt-2 space-y-1">
              {getEventsForDay(day).map((event) => (
                <div
                  key={event.id}
                  className="text-xs p-1 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{event.title}</span>
                    {getEventBadge(event.type)}
                  </div>
                  <div className="text-gray-500 text-[10px] mt-1">
                    {format(new Date(event.start_time), "HH:mm")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const DayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayEvents = getEventsForDay(currentDate);

    return (
      <div className="space-y-4">
        <div className="text-lg font-bold">
          {format(currentDate, "EEEE, d 'de' MMMM", { locale: es })}
        </div>
        <div className="space-y-2">
          {hours.map((hour) => {
            const hourEvents = dayEvents.filter(
              (event) => new Date(event.start_time).getHours() === hour
            );

            return (
              <div key={hour} className="flex items-start">
                <div className="w-16 text-sm text-gray-500">
                  {format(new Date().setHours(hour, 0), "HH:mm")}
                </div>
                <div className="flex-1 space-y-1">
                  {hourEvents.map((event) => (
                    <div
                      key={event.id}
                      className="p-2 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{event.title}</span>
                        {getEventBadge(event.type)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {format(new Date(event.start_time), "HH:mm")} -{" "}
                        {format(new Date(event.end_time), "HH:mm")}
                      </div>
                      {event.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {event.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-12rem)]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-6 w-6 text-slate-800" />
          <h2 className="text-2xl font-bold text-slate-900">Calendario Judicial & Despachos</h2>
        </div>

        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setViewCategory("procesal")}
              className={cn(
                "px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5",
                viewCategory === "procesal"
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              )}
            >
              <Gavel className="h-3.5 w-3.5 text-indigo-600" />
              Términos & Despachos
            </button>
            <button
              onClick={() => setViewCategory("general")}
              className={cn(
                "px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5",
                viewCategory === "general"
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              )}
            >
              <CalendarIcon className="h-3.5 w-3.5 text-slate-500" />
              Agenda General
            </button>
          </div>

          {viewCategory === "general" && (
            <Dialog
              open={isNewEventDialogOpen}
              onOpenChange={setIsNewEventDialogOpen}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsNewEventDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Evento
              </Button>
              <NewEventDialog
                onOpenChange={(open) => {
                  setIsNewEventDialogOpen(open);
                  if (!open) setEditingEvent(null);
                }}
                onSubmit={handleEventSubmit}
                userId={user.id || ""}
                initialData={editingEvent}
              />
            </Dialog>
          )}
        </div>
      </div>

      {viewCategory === "procesal" ? (
        <CalendarioProcesalDespachos />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button variant="ghost" size="icon" onClick={prevMonth}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-lg font-medium">
                    {format(currentDate, "MMMM yyyy", { locale: es })}
                  </h3>
                  <Button variant="ghost" size="icon" onClick={nextMonth}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Tabs
                  value={activeTab}
                  className="w-[200px]"
                  onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="day">Día</TabsTrigger>
                    <TabsTrigger value="week">Semana</TabsTrigger>
                    <TabsTrigger value="month">Mes</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              {activeTab === "month" && <MonthView />}
              {activeTab === "week" && <WeekView />}
              {activeTab === "day" && <DayView />}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Próximos Eventos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200">
                {filteredEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center gap-4 rounded-lg border p-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        {getEventBadge(event.type)}
                        <p className="font-medium">{event.title}</p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {format(new Date(event.start_time), "PPp")}
                      </div>
                      {event.description && (
                        <p className="text-sm text-gray-600">
                          {event.description}
                        </p>
                      )}
                      {event.clientName && (
                        <p className="text-sm text-gray-600">
                          Cliente: {event.clientName}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditEvent(event)}
                        className="h-8 w-8 p-0">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEvent(event)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Filtros</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    type="search"
                    placeholder="Buscar eventos..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de Evento</label>
                <Select
                  value={selectedEventType}
                  onValueChange={setSelectedEventType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="reunión">Reuniones</SelectItem>
                    <SelectItem value="vencimiento">Vencimientos</SelectItem>
                    <SelectItem value="documento">Documentos</SelectItem>
                    <SelectItem value="consulta">Consultas</SelectItem>
                    <SelectItem value="caso">Casos</SelectItem>
                    <SelectItem value="tarea">Tareas</SelectItem>
                    <SelectItem value="plazo">Plazos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingEvent}
        onOpenChange={() => setDeletingEvent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el
              evento <strong>"{deletingEvent?.title}"</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEvent}
              className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Calendar;
